import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import { styled, Theme } from '@mui/material/styles'
import { Typography, IconButton } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNewOutlined'
import Drawer from '../../control/Drawer'
import Slider from '../../control/Slider'
import Switch from '../../control/Switch'
import CheckBox from '../../control/Checkbox'
import Scrollbar from '../../control/Scrollbar'
import Grid from '@mui/material/Grid'

type FormMapLayerSettingProps = {
    onClose: () => void
    onOpacityLayerChange?: (item: MapCustomLayer, opacity: number) => void
    onVisibleLayerChange?: (item: MapCustomLayer, visible: boolean) => void
    onVisibleSublayerChange?: (item: MapCustomLayer, subLayerId: number, visible: boolean) => void
    customMapLayerItem?: MapCustomLayer
    isOpen: boolean
}

const FormMapLayerSetting = (props: FormMapLayerSettingProps) => {
    const { onClose, onOpacityLayerChange, onVisibleLayerChange, onVisibleSublayerChange, customMapLayerItem, isOpen } =
        props
    const { t } = useTranslation()
    const classes = useStyles()

    let type = ''
    switch (customMapLayerItem?.type) {
        case 'wms':
            type = 'WMS'
            break
        case 'wmts':
            type = 'WMTS'
            break
        case 'dynamic':
        case 'tile':
        case 'layer':
            type = 'AGS'
            break
        default:
            break
    }

    return (
        <Drawer
            isOpen={isOpen}
            inlineStyle={{ position: 'unset' }}
            hideBackdrop
            overrideClass={{ paper: classes.drawerPaper }}
        >
            {customMapLayerItem ? (
                <StyledDivContainer>
                    <StyledDivTitle>
                        <IconButton
                            aria-label="close"
                            onClick={onClose}
                            sx={{
                                position: 'absolute',
                                left: 10,
                                top: 15,
                            }}
                            className={classes.backIcon}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <StyledTypographyTitle variant="body2">
                            {t('LABEL.MAP.MAP_LAYER.SETTING.TITLE')}
                        </StyledTypographyTitle>
                    </StyledDivTitle>
                    <StyledContent>
                        <div style={{ width: '100%', height: '100%' }}>
                            <Scrollbar scrollbarTrackBorderRadius={10} scrollbarThumbBorderRadius={10}>
                                <StyledContentGroup>
                                    <StyledDivContent>
                                        <StyledDivMapSection>
                                            <StyledTypoMapName>{customMapLayerItem.name}</StyledTypoMapName>
                                            <StyledTypoMapType>{type}</StyledTypoMapType>
                                        </StyledDivMapSection>
                                        <StyledDivMapSection>
                                            <StyledTypoSection>
                                                {t('LABEL.MAP.MAP_LAYER.SETTING.OPACITY')}
                                            </StyledTypoSection>
                                            <Grid container>
                                                <Grid item xs={1}>
                                                    <Typography
                                                        variant={'body2'}
                                                        style={{ position: 'relative', top: 2 }}
                                                    >
                                                        {t('LABEL.MAP.MAP_LAYER.SETTING.TRANSPARENT')}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={10}>
                                                    <Slider
                                                        onChange={(opacity: number) => {
                                                            onOpacityLayerChange?.(customMapLayerItem, opacity)
                                                        }}
                                                        value={customMapLayerItem.opacity}
                                                    />
                                                </Grid>
                                                <Grid item xs={1}>
                                                    <Typography
                                                        variant={'body2'}
                                                        style={{ position: 'relative', left: 16, top: 2 }}
                                                    >
                                                        {t('LABEL.MAP.MAP_LAYER.SETTING.OPAQUE')}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </StyledDivMapSection>
                                        <StyledDivMapSection>
                                            <StyledTypoSection>
                                                {t('LABEL.MAP.MAP_LAYER.SETTING.DISPLAY')}
                                            </StyledTypoSection>
                                            <StyledDivSwitch>
                                                <Switch
                                                    labelOn={t('BUTTON.SHOW_RESULT')}
                                                    labelOff={t('BUTTON.HIDE_RESULT')}
                                                    checked={customMapLayerItem.visible}
                                                    onChange={(enabled: boolean) => {
                                                        onVisibleLayerChange?.(customMapLayerItem, enabled)
                                                    }}
                                                    width={80}
                                                    labelLeft={12}
                                                    labelRight={15}
                                                />
                                            </StyledDivSwitch>
                                        </StyledDivMapSection>
                                        {customMapLayerItem.subLayers ? (
                                            <StyledDivMapSection>
                                                <StyledTypoSection>
                                                    {t('LABEL.MAP.MAP_LAYER.SETTING.SUBLAYERS')}
                                                </StyledTypoSection>
                                                {customMapLayerItem.subLayers.map((subLayer, index) => {
                                                    return (
                                                        <CheckBox
                                                            key={'checkboxIndex' + index.toString()}
                                                            name={'checkBox' + index.toString()}
                                                            label={subLayer.name}
                                                            labelPlacement="end"
                                                            onClick={(checked) => {
                                                                onVisibleSublayerChange?.(
                                                                    customMapLayerItem,
                                                                    subLayer.id,
                                                                    checked,
                                                                )
                                                            }}
                                                            checked={subLayer.visible}
                                                            overrideClass={{
                                                                root: classes.checkbox,
                                                                checked: classes.checkboxChecked,
                                                            }}
                                                        />
                                                    )
                                                })}
                                            </StyledDivMapSection>
                                        ) : null}
                                    </StyledDivContent>
                                </StyledContentGroup>
                            </Scrollbar>
                        </div>
                    </StyledContent>
                </StyledDivContainer>
            ) : null}
        </Drawer>
    )
}

export default FormMapLayerSetting

const useStyles = makeStyles((theme: Theme) => ({
    drawerPaper: {
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    backIcon: {
        color: `${theme.palette.text.secondary} !important`,
    },
    divButton: {
        paddingTop: '24px',
        position: 'absolute',
        right: 0,
    },
    buttonAdd: {
        minWidth: '40px !important',
        minHeight: '40px !important',
        padding: '0px !important',
    },
    buttonSetting: {
        minWidth: '50px !important',
        padding: '0px !important',
    },
    deleteSetting: {
        minWidth: '35px !important',
        padding: '0px !important',
    },
    divider: {
        margin: '20px 0px !important',
    },
    lineClamp: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        '-webkit-line-clamp': 1,
        '-webkit-box-orient': 'vertical',
    },
    checkbox: {
        color: `${theme.palette.primary.dark} !important`,
    },
    checkboxChecked: {
        color: `${theme.palette.primary.dark} !important`,
    },
}))

const StyledDivContainer = styled('div')(({ theme }) => ({
    width: '350px',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
        width: '100%',
    },
}))

const StyledDivTitle = styled('div')(({ theme }) => ({
    width: '100%',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: '20px 0px',
    backgroundColor: theme.palette.secondary.main,
    boxShadow: theme.palette.getContrastText('shadow-bottom'),
}))

const StyledTypographyTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    textAlign: 'center',
}))

const StyledContent = styled('div')(({ theme }) => ({
    display: 'flex',
    height: `calc(100% - 68px)`,
}))

const StyledContentGroup = styled('div')(({ theme }) => ({
    margin: '20px',
}))

const StyledDivContent = styled('div')(({ theme }) => ({
    marginTop: '10px',
    marginBottom: '20px',
}))

const StyledDivMapSection = styled('div')(({ theme }) => ({
    padding: '5px 0px',
    width: '100%',
    marginBottom: 10,
}))

const StyledTypoSection = styled(Typography)(({ theme }) => ({
    color: theme.palette.secondary.dark,
}))

const StyledTypoMapName = styled(Typography)(({ theme }) => ({
    color: theme.palette.secondary.dark,
    fontWeight: 'bold',
}))

const StyledTypoMapType = styled(Typography)(({ theme }) => ({
    color: '#9A9A9A',
    marginTop: 5
}))


const StyledDivSwitch = styled('div')(({ theme }) => ({
    paddingTop: '10px',
}))
